import React, { ReactNode, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { Add, ExpandMore } from '@mui/icons-material'
import {
  Button,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material'

import {
  AISearchIcon,
  AdminSettingsIcon,
  AnalyticsIcon,
  CaseIcon,
  ContactBookIcon,
  ContentIcon,
  EditIcon,
  ListIcon,
  LogIcon,
  PaymentIcon,
  ProcessIcon,
  PublishIcon,
} from '~assets/icons'
import { images } from '~assets/images'
import { Avatar } from '~components/common'
import OrganizationSelector from '~components/common/OrganizationSelector'
import QuickCreateDialog from '~pages/QuickCreateDialog'
import { RootState } from '~redux/rootReducer'
import { setSelectedEIOrganization } from '~redux/slices/organizationSlice'
import { EmailIdentity, IdentityId, SkinEnum } from '~types'

import Image from '../common/Image'

import './layout.less'

const DRAWER_WIDTH = 240
const COLLAPSED_WIDTH = 65

const StyledSidebar = styled('div')<{ isExpanded: boolean }>(({ theme, isExpanded }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'relative',
  width: isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH,
  // Export sidebar width as a CSS variable for other components to use
  '--sidebar-width': `${isExpanded ? DRAWER_WIDTH : COLLAPSED_WIDTH}px`,
  flexShrink: 0,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create(['width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),
  '&:hover': {
    width: DRAWER_WIDTH,
    '& .MuiListItemText-root': {
      opacity: 1,
    },
    '& .expand-icon': {
      opacity: 1,
    },
  },
  '& .MuiListItemButton-root': {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  '& .MuiListItemIcon-root': {
    minWidth: 36,
    justifyContent: 'center',
  },
  '& .MuiListItemText-root': {
    opacity: isExpanded ? 1 : 0,
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    marginLeft: theme.spacing(1),
  },
  '& .MuiDivider-root': {
    margin: theme.spacing(1, 0),
  },
  '& .section-title': {
    opacity: isExpanded ? 0.7 : 0.5,
    transition: theme.transitions.create(['opacity', 'padding'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    padding: isExpanded ? theme.spacing(1, 2) : theme.spacing(1, 0),
    height: 'auto', // Always keep height to prevent layout shifts
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textAlign: isExpanded ? 'left' : 'center',
    whiteSpace: 'nowrap',
  },
  '& .expand-icon': {
    opacity: isExpanded ? 1 : 0,
    transition: theme.transitions.create(['opacity', 'transform'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      transition: theme.transitions.create(['transform'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.standard,
      }),
    },
  },
  '& .org-selector-container': {
    padding: isExpanded ? theme.spacing(0, 2) : 0,
    marginBottom: isExpanded ? theme.spacing(1) : 0,
    transition: theme.transitions.create(['opacity', 'height', 'padding', 'margin', 'visibility'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.standard,
    }),
  },
}))

type SidebarProps = {}

type SidebarItemProps = {
  icon?: React.ReactNode
  logo?: string
  label: string
  nav: string
  isExpanded?: boolean
  children?: Array<{
    label: string
    nav: string
    icon?: React.ReactNode
  }>
}

const SidebarItem = ({
  icon,
  label,
  logo,
  nav,
  children,
  isExpanded,
}: SidebarItemProps): ReactNode => {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  // For items with children, only highlight parent when collapsed
  // When expanded, prioritize child highlighting
  const isActive = children
    ? open
      ? false // Parent not highlighted when expanded
      : children.some((child) => location.pathname === child.nav) // Parent highlighted when collapsed and child matches
    : location.pathname === nav // Normal highlight for items without children

  const handleClick = (event: React.MouseEvent): void => {
    if (children) {
      event.preventDefault()
      setOpen(!open)
    }
  }

  return (
    <>
      <ListItemButton
        component={children ? 'div' : NavLink}
        to={children ? undefined : nav}
        onClick={handleClick}
        selected={isActive}
        sx={{
          alignItems: 'center',
        }}
      >
        <ListItemIcon>
          {icon ? (
            React.cloneElement(icon as React.ReactElement<{ gradient?: boolean }>, {
              gradient: isActive,
            })
          ) : logo ? (
            <Image src={logo} alt={label} styles='sidebar-icon' size='22px' />
          ) : null}
        </ListItemIcon>
        <ListItemText primary={label} />
        {children && (
          <div className='expand-icon' style={{ alignItems: 'center', display: 'flex' }}>
            <ExpandMore sx={{ transform: open ? 'rotate(0deg)' : 'rotate(-90deg)' }} />
          </div>
        )}
      </ListItemButton>
      {children && (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {children.map((child) => (
              <ListItemButton
                key={child.nav}
                component={NavLink}
                to={child.nav}
                selected={location.pathname === child.nav}
                sx={{
                  ml: isExpanded ? 2 : 0,
                }}
              >
                <ListItemIcon>
                  {child.icon &&
                    React.cloneElement(child.icon as React.ReactElement<{ gradient?: boolean }>, {
                      gradient: location.pathname === child.nav,
                    })}
                </ListItemIcon>
                <ListItemText primary={child.label} />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </>
  )
}

const Sidebar = ({}: SidebarProps): ReactNode => {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization,
  )
  const user = useSelector((state: RootState) => state?.auth.user)
  const [quickCreateOpen, setQuickCreateOpen] = useState<boolean>(false)
  const location = useLocation()

  // Check if current path is in the AI section
  const isInAiSection = useMemo(() => {
    if (!selectedOrganization?.handle) return false

    const aiPaths = ['query-log', 'analytics', 'sources', 'query-customize', 'query-publish']

    return aiPaths.some((path) =>
      location.pathname.includes(`/organizations/${selectedOrganization.handle}/${path}`),
    )
  }, [location.pathname, selectedOrganization?.handle])

  React.useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      isExpanded ? 'var(--sidebar-width-expanded)' : 'var(--sidebar-width-collapsed)',
    )
  }, [isExpanded])

  const toggleQuickCreateDialog = (): void => {
    setQuickCreateOpen(!quickCreateOpen)
  }

  if (user?.skin === SkinEnum.enum.Casual) {
    return (
      <StyledSidebar
        className='box-shadow-style do-not-print'
        isExpanded={isExpanded}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <List
          component='nav'
          sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
          <div style={{ flexGrow: 1 }} />
          <div>
            <SidebarItem
              icon={<CaseIcon color='var(--lightcarbon)' width={18} height={18} />}
              label='My Cases'
              nav='/my-cases'
            />
            <SidebarItem
              icon={
                <Avatar
                  identityId={user.identity_id as IdentityId}
                  alt={user.name || undefined}
                  size={'30px'}
                />
              }
              label={user.name || 'Account Settings'}
              nav='/account'
            />
          </div>
        </List>
      </StyledSidebar>
    )
  }

  return (
    <StyledSidebar
      className='box-shadow-style do-not-print'
      isExpanded={isExpanded}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <QuickCreateDialog open={quickCreateOpen} onClose={toggleQuickCreateDialog} />
      <List
        component='nav'
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      >
        {/* Main navigation items - scrollable */}
        <div style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <ListItemButton component='div' sx={{ '&.Mui-disabled': { opacity: 1 } }} disabled>
            <ListItemIcon>
              <Image src={images.logo} alt='Polimorphic' styles='sidebar-icon' size='22px' />
            </ListItemIcon>
            <ListItemText primary='Polimorphic' />
          </ListItemButton>
          <Divider />

          <div className='section-title'>{isExpanded ? 'CRM & Workflows' : 'CRM'}</div>
          <ListItemButton onClick={toggleQuickCreateDialog}>
            <ListItemIcon>
              <Button
                variant='contained'
                color='primary'
                style={{
                  width: '22px',
                  height: '22px',
                  minWidth: '22px',
                  padding: 0,
                }}
              >
                <Add style={{ color: 'white' }} />
              </Button>
            </ListItemIcon>
            <ListItemText primary='Quick Create' />
          </ListItemButton>
          <SidebarItem
            icon={<CaseIcon color='var(--lightcarbon)' width={18} height={18} />}
            label='Cases'
            nav='/cases'
          />
          <SidebarItem
            icon={<ContactBookIcon color='var(--lightcarbon)' width={18} />}
            label='Contacts'
            nav='/contacts'
          />
          <SidebarItem
            icon={<ProcessIcon color='var(--lightcarbon)' width={18} />}
            label='Processes'
            nav='/processes'
          />
          <SidebarItem
            icon={<PaymentIcon color='var(--lightcarbon)' width={18} />}
            label='Payments'
            nav='/bills'
          />
          <SidebarItem
            icon={<AnalyticsIcon color='var(--lightcarbon)' width={18} />}
            label='Analytics'
            nav='/analytics'
          />

          <Divider />
          <div className='section-title'>{isExpanded ? 'AI Front Desk' : 'AI'}</div>
          <div className='org-selector-container' style={{ position: 'relative' }}>
            {isExpanded ? (
              <OrganizationSelector
                orgHandle={selectedOrganization?.handle ?? undefined}
                onSelect={(org: EmailIdentity) => {
                  if (org.handle && org.handle !== selectedOrganization?.handle) {
                    dispatch(setSelectedEIOrganization(org))

                    // If we're already on an AI settings page, keep the same subpage but update org
                    const aiSettingsPages = [
                      'sources',
                      'query-customize',
                      'query-publish',
                      'query-log',
                      'analytics',
                      'settings/permissions',
                    ]

                    const currentPath = location.pathname
                    const currentPage = aiSettingsPages.find((page) =>
                      currentPath.includes(
                        `/organizations/${selectedOrganization?.handle}/${page}`,
                      ),
                    )

                    if (currentPage) {
                      navigate(`/organizations/${org.handle}/${currentPage}`)
                    } else {
                      // Default to sources if not on an AI settings page
                      navigate(`/organizations/${org.handle}/sources`)
                    }
                  }
                }}
                orgsOnly={true}
              />
            ) : (
              <SidebarItem
                icon={
                  <div
                    style={{
                      filter: isInAiSection ? 'none' : 'grayscale(100%)',
                      transition: 'filter 0.3s ease',
                    }}
                  >
                    <Avatar identityId={selectedOrganization?.id as IdentityId} size={'26px'} />
                  </div>
                }
                label={selectedOrganization?.name || ''}
                nav='/account'
              />
            )}
          </div>
          <SidebarItem
            icon={<LogIcon color='var(--lightcarbon)' width={18} />}
            label='Log'
            nav={`/organizations/${selectedOrganization?.handle}/query-log`}
          />
          <SidebarItem
            icon={<AnalyticsIcon color='var(--lightcarbon)' width={18} />}
            label='Analytics'
            nav={`/organizations/${selectedOrganization?.handle}/analytics`}
          />
          <SidebarItem
            icon={<AISearchIcon color='var(--lightcarbon)' width={18} />}
            label='Settings'
            nav={`/organizations/${selectedOrganization?.handle}/sources`}
            isExpanded={isExpanded}
            children={[
              {
                label: 'Content',
                nav: `/organizations/${selectedOrganization?.handle}/sources`,
                icon: <ContentIcon color='var(--lightcarbon)' width={18} />,
              },
              {
                label: 'Customize',
                nav: `/organizations/${selectedOrganization?.handle}/query-customize`,
                icon: <EditIcon color='var(--lightcarbon)' width={18} />,
              },
              {
                label: 'Publish',
                nav: `/organizations/${selectedOrganization?.handle}/query-publish`,
                icon: <PublishIcon color='var(--lightcarbon)' width={18} />,
              },
              {
                label: 'Admin Settings',
                nav: `/organizations/${selectedOrganization?.handle}/settings/permissions`,
                icon: <AdminSettingsIcon color='var(--lightcarbon)' width={18} />,
              },
            ]}
          />
        </div>
        {/* Spacer to push bottom items down */}
        <div style={{ flexGrow: 1 }} />
        {/* Bottom pinned items */}
        <div>
          <Divider />
          <SidebarItem
            icon={<ListIcon color='var(--lightcarbon)' width={18} />}
            label='Menu'
            nav='/menu'
          />
          {user ? (
            <SidebarItem
              icon={
                <Avatar
                  identityId={user.identity_id as IdentityId}
                  alt={user.name || undefined}
                  size={'26px'}
                />
              }
              label={user.name || 'Account Settings'}
              nav='/account'
            />
          ) : (
            <></>
          )}
        </div>
      </List>
    </StyledSidebar>
  )
}

export default Sidebar
